import oidc, { UserManager } from 'oidc-client';
import 'jquery';
import 'bootstrap/dist/js/bootstrap';
import 'react-dates/initialize';
import angular from 'angular';
import log from 'loglevel';

import './ajs';
import './style/main.scss';
import zendesk from './zendesk';
import config from './config';
import { onCkEditorLoaded } from './ckeditorConfig';
import { initAppInsights } from './appInsights';
import userflow from 'userflow.js';

import AuthService from './shared/services/AuthService';
import TimerRegistrationService from './shared/services/TimerRegistrationService';
import SignalRService from './shared/services/SignalRService';

if (config.environment !== 'local') {
  // Force https
  if (window.location.protocol !== 'https:') {
    window.location.href = `https:${window.window.location.href.substring(
      window.location.protocol.length,
    )}`;
  }
}

// dump config in dev environment
if (config.environment !== 'Production' && config.environment !== 'Acceptance') {
  console.log('App started with');
  console.log('  version:          ', config.version);
  console.log('  environment:      ', config.environment);
  console.log('  build:            ', config.buildType);
  console.log('  app insights:     ', config.appInsightsKey);
  console.log('  logLevel:         ', config.logLevel);

  console.log('  appUrls:          ', { ...config.appUrls });
  console.log('  auth:             ', { ...config.auth });
  console.log('  features:         ', { ...config.features });

  // Enable oidc logging in dev
  oidc.Log.logger = console;
}

// setup logging
log.setDefaultLevel(config.logLevel);
log.setLevel(config.logLevel);

// initialize Beamer whatsnew
function initializeBeamerWhatsNew(user) {
  const { Sid, Name, Email, l, su } = user.profile;
  if (su) return; // dont init beamer for support user

  const configScript = document.createElement('script');
  const beamerConfig = {
    product_id: 'ngvcLvvG30699',
    selector: '.beamerTrigger',
    display: 'right',
    top: 10, // only numbers are allowed, not '-5px'
    language: l || 'nl', // init with language from token, later on actual (current) language will be used to re-init config
    lazy: true,
    alert: true,
    user_email: Email,
    user_id: Sid,
  };
  configScript.type = 'text/javascript';
  configScript.text = `var beamer_config = ${JSON.stringify(beamerConfig)}`;
  document.body.appendChild(configScript);

  const urlScript = document.createElement('script');
  urlScript.src = `https://app.getbeamer.com/js/beamer-embed.js?t=${Date.now()}`;
  urlScript.defer = 'defer';
  document.body.appendChild(urlScript);
}

const setupZendeskTags = user => {
  // this method adds the tenantId to the zendesk tags. This makes it easier for support agents to find more info about the tenant requesting help
  if (user.profile && user.profile.GroupSid) {
    const {
      GroupSid: tenantId,
      Name: userName,
      Email: userEmail,
      TenantConfiguration,
    } = user.profile;

    zendesk.setUserInfo(tenantId, userName, userEmail, TenantConfiguration);
  }
};
const initUserflow = (apiKey, user) => {
  const { su } = user.profile;
  if (su) return; // dont init beamer for support user
  userflow.init(apiKey);
};
// log in before angular startup
const manager = new UserManager(config.auth);
manager.clearStaleState();

// when opening a new tab the getUser will return null and authentication should be performed
manager.getUser().then(user => {
  if (!user) {
    manager.signinRedirect({
      state: {
        url: window.location.href,
      },
    });
  } else {
    manager.events.addAccessTokenExpired(() => {
      console.debug('Access token expired!');
      SignalRService.stopConnection();
      manager.signinRedirect({
        state: {
          url: window.location.href,
        },
      });
    });
    manager.events.addSilentRenewError(err => {
      if (err && err.message === 'login_required') {
        SignalRService.stopConnection();
        manager.signoutRedirect();
      } else {
        console.error('SilentRenewError', err);
      }
    });

    // called on silent login callback
    manager.events.addUserLoaded(newUser => {
      // ccc = credentials changed count. Logout the user if their credentials have changed after the silent login
      if (
        user?.profile &&
        newUser?.profile &&
        (user.profile.ccc || user.profile.ccc === 0) &&
        parseInt(newUser.profile.ccc) > parseInt(user.profile.ccc)
      ) {
        SignalRService.stopConnection();
        manager.signoutRedirect();
      }

      if (user && user.profile && user.profile.sub !== newUser.profile.sub) {
        console.debug(
          `Tenant changed from ${user.profile.sub} to ${newUser.profile.sub}. Reloading...`,
        );
        SignalRService.stopConnection();
        window.location.reload();
      }
    });

    setupZendeskTags(user);
    initializeBeamerWhatsNew(user);
    applyNodeChildFix();
    // initialize userflow
    if (config.userflowKey && config.userflowKey !== 'null') {
      initUserflow(config.userflowKey, user);
      window.userflowInitialized = true;
    }
    // Initialize application insights -
    // - https://github.com/microsoft/ApplicationInsights-JS
    // - https://github.com/microsoft/ApplicationInsights-JS/blob/master/API-reference.md
    if (config.appInsightsKey && config.appInsightsKey !== 'null') {
      const appInsights = initAppInsights(config.appInsightsKey, user);
      angular.module('cloudX').constant('appInsights', appInsights);
    } else {
      angular.module('cloudX').constant('appInsights', null);
    }

    angular.module('cloudX').constant('appUrls', config.appUrls);
    angular.module('cloudX').constant('environment', config.environment);
    angular.module('cloudX').constant('buildversion', config.buildVersion);
    angular.module('cloudX').constant('featureflags', config.features);
    // Only to be used by the AuthService
    angular.module('cloudX').constant('authServiceConfig', {
      user,
      manager,
    });

    AuthService.setConfig({
      user,
      manager,
    });

    appendScript(`${config.appUrls.storage}/ckeditor_4.21.0/ckeditor.js`).then(
      () => onCkEditorLoaded(),
      error => {
        console.error(error);
      },
    );

    TimerRegistrationService.init();

    window.addEventListener('beforeunload', () => {
      SignalRService.stopConnection();
    });

    // start angular app
    angular.bootstrap(document, ['cloudX'], {
      strictDi: false,
    });
  }
});

function appendScript(url, defer, async) {
  const script = document.createElement('script');
  script.src = url;

  if (defer) {
    script.defer = true;
  }
  if (async) {
    script.async = true;
  }
  document.body.appendChild(script);
  return new Promise((res, rej) => {
    script.onload = () => res();
    script.onerror = e => rej(e);
  });
}

// inject google map api
appendScript(
  `https://maps.googleapis.com/maps/api/js?key=${config.googleApiKey}&callback=Function.prototype`,
  true,
);
appendScript(
  'https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/17.0.0/js/intlTelInput.min.js',
  true,
);
appendScript('https://www.google.com/recaptcha/enterprise.js?render=explicit', true, true);

appendScript('https://acrobatservices.adobe.com/view-sdk/viewer.js', true);

if (config.environment !== 'local') {
  const subdomain =
    config.environment === 'Development'
      ? 'dev'
      : config.environment === 'Acceptance'
      ? 'acc'
      : 'app';

  document.write(
    `<script defer data-domain="${subdomain}.adminpulse.be" data-api="/ps/api/event" src="/ps/js/script.manual.js"></script>`,
  );

  //  define the `plausible` function to manually trigger events
  document.write(
    '<script>window.plausible = window.plausible || function() { (window.plausible.q = window.plausible.q || []).push(arguments) }</script>',
  );
}

// some extensions like 3CX click 2 call cause our app to crash
// this is a problem in react screens/popups
// at the time of writing this, there is no other solution except for this nasty fix
// we already disabled Google translate on the entire web app, Google translate is not the cause of this issue for us.
// Userflow's Resource Center renders its own React tree on document.body; the silent-swallow
// fallback corrupts its fiber/DOM state and triggers React error #185 (infinite update loop),
// which unmounts the widget. We detect Userflow's subtree (custom element or id prefix
// `userflow-`) and rethrow the original error there so its reconciler can recover normally.
// If Userflow ever renames its host element, update isInsideUserflow accordingly.
function isInsideUserflow(node) {
  let n = node;
  while (n) {
    const tag = n.tagName ? n.tagName.toLowerCase() : '';
    if (tag.startsWith('userflow')) return true;
    if (typeof n.id === 'string' && n.id.toLowerCase().startsWith('userflow-')) return true;
    n = n.parentNode || (n.host ?? null);
  }
  return false;
}

function applyNodeChildFix() {
  if (typeof window !== 'undefined' && !window.__googleTranslateFixApplied) {
    const originalRemoveChild = Node.prototype.removeChild;

    Node.prototype.removeChild = function(child) {
      try {
        return originalRemoveChild.call(this, child);
      } catch (error) {
        if (error.name === 'NotFoundError' && !isInsideUserflow(this)) {
          // Prevents crashes by ignoring the error
          return child;
        }
        throw error;
      }
    };

    const originalInsertBefore = Node.prototype.insertBefore;

    Node.prototype.insertBefore = function(newNode, referenceNode) {
      try {
        return originalInsertBefore.call(this, newNode, referenceNode);
      } catch (error) {
        if (error.name === 'NotFoundError' && !isInsideUserflow(this)) {
          // If insertion fails, append the element instead
          return this.appendChild(newNode);
        }
        throw error;
      }
    };

    window.__googleTranslateFixApplied = true;
  }
}
